import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

interface SignInAttempt {
  id: number;
  email: string;
  succeeded: boolean;
  outcome: string;
  created_at: string;
}

const LABELS: Record<string, string> = {
  signed_in: 'Signed in',
  unknown_email: 'Not an account',
  inactive: 'Inactive account',
  bad_password: 'Wrong password',
  bad_code: 'Wrong code',
  rejected: 'Rejected',
};

@customElement('fp3-sign-ins')
export class Fp3SignIns extends LitElement {
  @state() private attempts: SignInAttempt[] = [];
  @state() private notAllowed = 0;
  @state() private error = '';

  static styles = css`
    :host { display: block; }
    h2 { font-size: 1.2rem; font-weight: 600; margin: 0 0 0.3rem; letter-spacing: -0.02em; }
    .subtitle { color: var(--dash-muted, #8fa3b8); font-size: 0.9rem; line-height: 1.45; margin: 0 0 1rem; }
    .error { color: #f0a8a8; }
    .warn {
      margin: 0 0 1rem;
      padding: 0.75rem 0.9rem;
      border: 1px solid rgba(224, 122, 122, 0.4);
      border-radius: 12px;
      background: rgba(224, 122, 122, 0.08);
      color: #f0c2c2;
    }
    .table-wrap {
      overflow: auto;
      max-height: min(68vh, 720px);
      border: 1px solid var(--dash-border, #314255);
      border-radius: 12px;
      background: var(--dash-surface, #121a22);
    }
    table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
    th, td {
      text-align: left;
      padding: 0.75rem 0.9rem;
      border-bottom: 1px solid #1e2a36;
      vertical-align: middle;
    }
    th {
      position: sticky;
      top: 0;
      font-size: 0.68rem;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--dash-muted, #8fa3b8);
      background: var(--dash-elevated, #182430);
    }
    tbody tr:hover td { background: rgba(91, 159, 212, 0.06); }
    tr.bad td { background: rgba(224, 122, 122, 0.07); }
    .when { white-space: nowrap; font-variant-numeric: tabular-nums; color: var(--dash-muted, #8fa3b8); }
    .pill {
      display: inline-flex;
      align-items: center;
      padding: 0.18rem 0.55rem;
      border-radius: 999px;
      font-size: 0.78rem;
      font-weight: 650;
      background: var(--dash-elevated, #182430);
      color: var(--dash-text, #e8edf2);
    }
    .ok .pill { background: rgba(143, 208, 168, 0.14); color: var(--dash-good, #8fd0a8); }
    .bad .pill { background: rgba(240, 168, 168, 0.14); color: #f0a8a8; }
    .empty { color: var(--dash-muted, #8fa3b8); padding: 1.4rem 0.9rem; }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.reload();
  }

  private async reload() {
    this.error = '';
    const res = await fetch('/api/admin/sign-ins');
    if (!res.ok) {
      this.error = 'Sign-in logs are not available.';
      return;
    }
    const body = await res.json();
    this.attempts = body.attempts ?? [];
    this.notAllowed = Number(body.not_allowed ?? 0);
  }

  private label(outcome: string) {
    return LABELS[outcome] ?? outcome;
  }

  private when(iso: string) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleString();
  }

  render() {
    return html`
      <h2>Sign in logs</h2>
      <p class="subtitle">Newest first. A signed-in row is someone who finished the text code. Not an account and Inactive account are emails that are not allowed in.</p>
      ${this.error ? html`<p class="error">${this.error}</p>` : nothing}
      ${this.notAllowed
        ? html`<p class="warn">${this.notAllowed} of these attempts used an email that is not allowed in.</p>`
        : nothing}
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Email</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            ${this.attempts.length
              ? this.attempts.map((row) => {
                  const refused = row.outcome === 'unknown_email' || row.outcome === 'inactive';
                  const ok = row.outcome === 'signed_in';
                  return html`
                    <tr class=${refused ? 'bad' : ok ? 'ok' : ''}>
                      <td class="when">${this.when(row.created_at)}</td>
                      <td>${row.email}</td>
                      <td><span class="pill">${this.label(row.outcome)}</span></td>
                    </tr>
                  `;
                })
              : html`<tr><td class="empty" colspan="3">No sign-in attempts yet.</td></tr>`}
          </tbody>
        </table>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fp3-sign-ins': Fp3SignIns;
  }
}
